import { useEffect, useState } from 'react';
import styles from './sportModuleCard.module.css';
import { Icons } from '../../../../code/IconHandler';
import { blue } from '@mui/material/colors';

export default function SportmodulCard({ modul, onEdit, onDelete }) {
    const [contacts, setContacts] = useState([]);
    const [locations, setLocations] = useState({}); // Store locations by id
    const blueCategories = ['reha', 'praevention', 'firmenfitness'];

    const category = modul.modules?.[0]?.category || '';

    const isBlueCategory = blueCategories.includes(category);

    useEffect(() => {
        // Fetch contacts
        const fetchContacts = async () => {
            const promises = modul.assignments.map(async (a) => {
                const res = await fetch(`/api/contact/${a.contact_id}`);
                return res.ok ? res.json() : null;
            });
            const resolved = await Promise.all(promises);
            const validContacts = resolved.filter(c => c !== null);
            setContacts(validContacts);
        };
        fetchContacts();
    }, [modul.assignments]);

    useEffect(() => {
        // Fetch locations for all appointments
        const uniqueLocationIds = [...new Set(modul.appointments.map(a => a.location_id).filter(Boolean))];
        if (uniqueLocationIds.length === 0) return;

        const fetchLocations = async () => {
            const promises = uniqueLocationIds.map(async (id) => {
                const res = await fetch(`/api/sportstaetten/${id}`);
                if (!res.ok) return null;
                const loc = await res.json();
                return [id, loc];
            });

            const results = await Promise.all(promises);
            // Convert to object { id: location }
            const locObj = {};
            results.forEach(r => {
                if (r) locObj[r[0]] = r[1];
            });
            setLocations(locObj);
        };

        fetchLocations();
    }, [modul.appointments]);

    return (
        <div
            className={`${styles.card} ${isBlueCategory ? styles['card--blue'] : ''}`}
        >
            {/* Row 1: Names and buttons */}
            <div className={styles.topRow}>
                <div className={styles.submodules}>
                    {modul.modules.map((mod) => (
                        <span key={mod.id} className={styles.submoduleItem}>
                            {mod.iconPath && (
                                <img
                                    src={mod.iconPath}
                                    alt={mod.iconText}
                                    className={styles.submoduleIcon}
                                />
                            )}
                            {mod.title}
                        </span>
                    ))}
                </div>

                <div className={styles.actions}>
                    <button className={styles.editBtn} onClick={() => onEdit(modul)}>
                        <Icons.Edit />
                    </button>
                    <button className={styles.deleteBtn} onClick={() => onDelete(modul.id)}>
                        <Icons.Delete />
                    </button>
                </div>
            </div>

            {/* Middle and Bottom combined with left/right columns */}
            <div className={styles.infoRow}>
                <div className={styles.leftColumn}>
                    <div className={styles.contacts}>
                        <p>                        <strong>Personen:</strong>{' '}
                            {contacts.length > 0
                                ? contacts.map((c) => `${c.first_name} ${c.family_name}`).join(', ')
                                : 'Lade Personen...'}
                        </p>
                    </div>

                    <div
                        className={`${styles.detailsWrapper} ${isBlueCategory ? styles.blue : styles.green}`}
                    >
                        <div className={styles.details}>
                            {modul.main_text && <span title="Haupttext">📄</span>}
                            {modul.kosten && <span title="Kosten">💰</span>}
                            {modul.mitbringen && <span title="Mitbringen">🎒</span>}
                            {modul.infos_lehrer && <span title="Infos Lehrer">👨‍🏫</span>}
                        </div>
                    </div>
                </div>

                <div className={styles.rightColumn}>
                    {modul.appointments.slice() // make a copy before sorting
                        .sort((a, b) => {
                            const weekdayOrder = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];

                            // Get recurrence day index
                            const getDayIndex = (r) => {
                                const match = r?.match(/BYDAY=(\w{2})/);
                                return match ? weekdayOrder.indexOf(match[1]) : -1;
                            };

                            const dayA = getDayIndex(a.recurrence_rule);
                            const dayB = getDayIndex(b.recurrence_rule);
                            if (dayA !== dayB) return dayA - dayB;

                            // Compare appointment_date (if exists)
                            const dateA = a.appointment_date || '';
                            const dateB = b.appointment_date || '';
                            if (dateA !== dateB) return dateA.localeCompare(dateB);

                            // Compare start_time
                            return a.start_time.localeCompare(b.start_time);
                        }).map((a) => {
                            const loc = locations[a.location_id];
                            const locationText = loc
                                ? loc.bezeichnung
                                    ? `${loc.bezeichnung}: ${loc.name}`
                                    : loc.name
                                : 'Keine Sportstätte angegeben';
                            console.log(a);


                            return (
                                <div key={a.id} className={styles.appointmentItem}>
                                    <p>{`${getReadableRecurrence(a.recurrence_rule, a.appointment_date)}: ${a.start_time.slice(0, 5)} - ${a.end_time.slice(0, 5)} | ${locationText}`}</p>
                                </div>
                            );
                        })}
                </div>
            </div>
        </div>
    );
}

function getReadableRecurrence(rule, appointment_date) {
    const map = {
        MO: 'Montag',
        TU: 'Dienstag',
        WE: 'Mittwoch',
        TH: 'Donnerstag',
        FR: 'Freitag',
        SA: 'Samstag',
        SU: 'Sonntag',
    };

    if (!rule || rule.trim() === '') {
        if (!appointment_date) return 'Einmalig';
        const date = new Date(appointment_date);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `Einmalig am ${day}.${month}.${year}`;
    }

    const freqMatch = rule.match(/FREQ=([^;]+)/);
    if (!freqMatch) {
        // No frequency means no recurrence
        return 'Einmalig';
    }

    const freq = freqMatch[1];

    if (freq === 'WEEKLY') {
        const byDayMatch = rule.match(/BYDAY=([^;]+)/);
        if (!byDayMatch) {
            // Weekly recurrence without days - fallback
            return 'Wöchentlich';
        }
        return byDayMatch[1]
            .split(',')
            .map((d) => map[d] || d)
            .join(', ');
    }

    // Handle other frequencies with simple readable names
    const freqMap = {
        DAILY: 'Täglich',
        MONTHLY: 'Monatlich',
        YEARLY: 'Jährlich',
    };

    if (freqMap[freq]) {
        return freqMap[freq];
    }

    // If nothing matches, fallback
    return 'Einmalig';
}

